import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ticket, Clock, CheckCircle2, Loader2, Mail, Building2, User, XCircle, Wrench, Image as ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-amber-500/10 text-amber-700 border-amber-200" },
  in_progress: { label: "In Progress", color: "bg-blue-500/10 text-blue-700 border-blue-200" },
  resolved: { label: "Resolved", color: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed", color: "bg-gray-500/10 text-gray-600 border-gray-200" },
};

export const SupportTicketsView = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchTickets = async () => {
    try {
      const r = await axios.get(`${API}/support-tickets`);
      setTickets(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchTickets(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/support-tickets/${id}/status`, { status });
      toast.success(status === "resolved" ? "Resolved — user notified by email" : `Marked as ${STATUS_CONFIG[status]?.label || status}`);
      fetchTickets();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = filter === "all" ? tickets : tickets.filter(t => t.status === filter);
  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === "open").length,
    in_progress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved").length,
  };

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="support-tickets-title">
          <Ticket className="h-5 w-5 text-primary" />Support Tickets
        </h2>
        <p className="text-sm text-muted-foreground">{tickets.length} total, {counts.open} open</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: "all", label: `All (${counts.all})` },
          { key: "open", label: `Open (${counts.open})` },
          { key: "in_progress", label: `In Progress (${counts.in_progress})` },
          { key: "resolved", label: `Resolved (${counts.resolved})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`ticket-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === f.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Ticket className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{filter === "all" ? "No support tickets yet" : `No ${STATUS_CONFIG[filter]?.label?.toLowerCase() || filter} tickets`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(ticket => {
            const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            return (
              <div key={ticket.id} className="bg-card border rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`support-ticket-${ticket.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{ticket.name}</span>
                      <Badge variant="outline" className={`text-xs ${statusCfg.color}`}>{statusCfg.label}</Badge>
                      <span className="text-xs text-muted-foreground font-mono">#{ticket.id?.slice(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <a href={`mailto:${ticket.email}`} className="flex items-center gap-1 hover:text-primary transition-colors"><Mail className="h-3 w-3" />{ticket.email}</a>
                      {ticket.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{ticket.company}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                    <Clock className="h-3 w-3" />
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </div>
                </div>

                <p className="text-sm bg-muted/40 rounded-lg p-3 mb-3 whitespace-pre-wrap">{ticket.description}</p>

                {ticket.screenshots?.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {ticket.screenshots.map((ss, i) => (
                      <a key={i} href={`${API}/attachments/${ss.filename}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-primary/5 text-primary rounded-lg border border-primary/20 hover:bg-primary/10 transition-colors">
                        <ImageIcon className="h-3.5 w-3.5" />{ss.original_name || `Screenshot ${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  {ticket.status === "open" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, "in_progress")} className="text-xs h-7" data-testid={`ticket-in-progress-${ticket.id}`}>
                      <Wrench className="h-3 w-3 mr-1" />Start Working
                    </Button>
                  )}
                  {(ticket.status === "open" || ticket.status === "in_progress") && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, "resolved")} className="text-xs h-7 text-emerald-600" data-testid={`ticket-resolve-${ticket.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />Mark Resolved
                    </Button>
                  )}
                  {ticket.status === "resolved" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(ticket.id, "open")} className="text-xs h-7">
                      <Clock className="h-3 w-3 mr-1" />Reopen
                    </Button>
                  )}
                  {ticket.status !== "closed" && (
                    <Button size="sm" variant="ghost" onClick={() => updateStatus(ticket.id, "closed")} className="text-xs h-7 text-muted-foreground">
                      <XCircle className="h-3 w-3 mr-1" />Close
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
